import { useMemo } from 'react';
import { useGetAccountsQuery } from '../../../redux/api/fymAPI';
import { useAccountId, useUserToken } from '../../../redux/slices/appInfo/hooks';
import { usePublishedPagesUsage } from '../../smartLinks/usePublishedPagesUsage';
import { useMusic24Capabilities } from '../../music24/hooks/useMusic24Capabilities';
import { useTeam } from '../../team/useTeam';
import { countOwnArtists } from '../../accountSelector/demoAccounts';

export interface SubscriptionRowUsage {
  /** `undefined` while the count is still loading. */
  used: number | undefined;
  limit: number;
}

export type SubscriptionUsageRowKey = 'pages' | 'artists' | 'seats';

/**
 * Usage for the three `MUSIC24_FEATURE_ROWS` rows a client endpoint already answers today:
 * published pages (`usePublishedPagesUsage`, from Task 3), artists (the accounts list) and team
 * seats (`team.list`, which already includes pending invites the way `TeamTab` counts them).
 *
 * Every other counted row — connected social accounts, scheduled auto-posts, video renders,
 * AI insights — has no client-facing read endpoint yet (only a server-to-server quota ledger).
 * This hook leaves those out on purpose, so `SubscriptionFeaturesList` falls back to the plain
 * plan limit for them; Task 5's report lists this as a follow-up.
 */
export function useSubscriptionUsage(): Partial<Record<SubscriptionUsageRowKey, SubscriptionRowUsage>> {
  const capabilities = useMusic24Capabilities();
  const userToken = useUserToken();
  const activeAccountId = useAccountId();
  // `usePublishedPagesUsage` needs a concrete account id to key its queries. Every workspace has
  // at least one artist by the time Settings renders, so `activeAccountId` is only ever `null` for
  // the rare instant before the first account loads — the `undefined` fallback below covers it.
  const pagesUsage = usePublishedPagesUsage(activeAccountId ?? 0, { skip: activeAccountId === null });

  const { accounts } = useGetAccountsQuery(undefined, {
    skip: !userToken,
    selectFromResult: ({ data }) => ({ accounts: data?.accounts }),
  });

  const team = useTeam();

  return useMemo(() => {
    const usage: Partial<Record<SubscriptionUsageRowKey, SubscriptionRowUsage>> = {};

    if (capabilities.publishedPagesLimit !== null) {
      usage.pages = {
        used: activeAccountId === null ? undefined : pagesUsage.used,
        limit: capabilities.publishedPagesLimit,
      };
    }

    if (capabilities.artistsLimit !== null) {
      // design: music24-demo-artist a1. The demo artist does not count.
      usage.artists = { used: countOwnArtists(accounts), limit: capabilities.artistsLimit };
    }

    if (capabilities.seatsLimit !== null) {
      usage.seats = {
        used: team.data === undefined ? undefined : team.data.members.length + team.data.pendingInvites.length,
        limit: capabilities.seatsLimit,
      };
    }

    return usage;
  }, [capabilities, activeAccountId, pagesUsage, accounts, team.data]);
}
